// MagicApprove — public page for email-link approvals (no login required)
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { API, formatCurrency } from '../utils/api';

const MagicApprove = () => {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!token) { setError('Missing token'); setLoading(false); return; }
    axios.get(`${API}/magic/verify?token=${encodeURIComponent(token)}`)
      .then(r => setInfo(r.data))
      .catch(err => setError(err.response?.data?.detail || 'Verification failed'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleAct = async () => {
    if (info.action === 'reject' && !note.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/magic/act`, { token, note });
      setDone(r.data);
      toast.success(r.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally { setSubmitting(false); }
  };

  const wrap = (children) => (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f4f5f7', padding: 24 }}>
      <div style={{ background: 'white', borderRadius: 16, padding: 32, maxWidth: 500, width: '100%', boxShadow: '0 10px 40px rgba(0,0,0,0.08)' }}>
        {children}
      </div>
    </div>
  );

  if (loading) return wrap(<div style={{ textAlign: 'center' }}><Loader2 className="w-8 h-8 animate-spin" /></div>);
  if (error) return wrap(<div><h2 style={{ color: '#c0392b' }}><XCircle className="inline w-6 h-6 mr-2" />Link Invalid</h2><p>{error}</p></div>);
  if (done) return wrap(<div style={{ textAlign: 'center' }}><CheckCircle className="w-16 h-16 mx-auto" style={{ color: '#27ae60' }} /><h2 style={{ marginTop: 16 }}>{done.message}</h2></div>);

  const isApprove = info.action === 'approve';

  return wrap(
    <div>
      <div style={{ marginBottom: 16, fontSize: 12, color: '#888' }}>Signed in as: <b>{info.approver.name}</b> ({info.approver.role_label})</div>
      <h2 style={{ color: isApprove ? '#0f2a5a' : '#c0392b', marginTop: 0 }}>
        {isApprove ? 'Approve Work Order' : 'Reject Work Order'}
      </h2>
      <div style={{ background: '#f4f5f7', padding: 16, borderRadius: 8, marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>{info.contract.contract_number}</div>
        <div style={{ fontSize: 13, color: '#555' }}>Vendor: {info.contract.vendor}</div>
        <div style={{ fontSize: 13, color: '#555' }}>Monthly Value: {formatCurrency(info.contract.monthly_value)}</div>
      </div>
      {!isApprove && (
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Rejection Reason *</label>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Why are you rejecting this?" data-testid="magic-note" />
        </div>
      )}
      <Button onClick={handleAct} disabled={submitting} data-testid="magic-confirm"
              style={{ width: '100%', background: isApprove ? '#27ae60' : '#c0392b', color: 'white' }}>
        {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
        Confirm {isApprove ? 'Approval' : 'Rejection'}
      </Button>
      <p style={{ fontSize: 11, color: '#888', textAlign: 'center', marginTop: 12 }}>Link expires: {new Date(info.expires_at).toLocaleString()}</p>
    </div>
  );
};

export default MagicApprove;
